import { useState, useEffect } from 'react';
import { useDemoMode } from '../../hooks/useDemoMode';
import { useAuth } from '../../contexts/AuthContext';
import AuthForm from '../auth/AuthForm';
import WarningModal from './WarningModal';

/**
 * Global demo handler component that manages demo-related UI states.
 * This component listens for demo events and shows the signup modal when needed.
 */
const DemoHandler = () => {
  const [showModal, setShowModal] = useState(true);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const { isDemoMode } = useDemoMode();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isDemoMode) return;

    // Listen for demo signup modal events
    const handleShowSignup = (event: CustomEvent) => {
      setShowModal(true);
    };

    window.addEventListener('show-demo-signup', handleShowSignup as EventListener);

    return () => {
      window.removeEventListener('show-demo-signup', handleShowSignup as EventListener);
    };
  }, [isDemoMode]);

  // Close modal when user becomes authenticated
  // Migration is now handled globally in AuthContext
  useEffect(() => {
    if (isAuthenticated && showModal) {
      console.log('User authenticated, closing demo signup modal');
      handleCloseModal();
    }
  }, [isAuthenticated, showModal]);

  const handleCloseModal = () => {
    setShowModal(false);
    setShowWarningModal(false);
  };

  const handleAttemptClose = () => {
    setShowWarningModal(true);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Only close if clicking the overlay itself, not the modal content
    if (e.target === e.currentTarget) {
      handleAttemptClose();
    }
  };

  const handleWarningSignUp = () => {
    setShowWarningModal(false);
    // Keep the auth modal open for signup
  };


  const handleWarningExit = () => {
    handleCloseModal();
  };

  // Handle successful authentication from AuthForm callback
  const handleAuthSuccess = () => {
    console.log('Auth success callback triggered');
    // Modal will be closed by the useEffect above when isAuthenticated changes
  };

  if (!isDemoMode) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4"
        onClick={handleOverlayClick}
      >
        <div className="relative max-w-lg w-full">
          {/* Use existing AuthForm component in compact mode */}
          <AuthForm 
            compact={true}
            onSuccess={handleAuthSuccess}
            onStateChange={(state) => {
              // Handle any state changes if needed
              console.log('Auth state changed:', state);
            }}
          />
        </div>
      </div>

      <WarningModal
        isOpen={showWarningModal}
        onSignUp={handleWarningSignUp}
        onExit={handleWarningExit}
      />
    </>
  );
};

export default DemoHandler;